import {
  composeOfacLegalName,
  composeOfacPlace,
  ofacAddressLayout,
  ofacDetectNameStyle,
  ofacLegalNameSearchVariants,
  ofacNationalIdTypes,
  ofacUsesUsPeopleDirectories,
  type OfacLanguage,
  type OfacNameStyle,
} from './ofacIntakeCatalog';
import { normalizeAgeRangeForSubmit } from './researchAgeRange';

export type ResearchPersonAgeBasis = 'birthdate' | 'ageRange';
export type ResearchPersonGender = '' | 'male' | 'female';

export type ResearchPersonKeyFields = {
  searchCountry: string;
  nationality: string;
  nationalities: string;
  gender: ResearchPersonGender;
  includeMaidenName?: boolean;
  addressLine: string;
  addressLine2: string;
  postalCode: string;
  nameStyle: OfacNameStyle | '';
  nameStyleLocked?: boolean;
  givenName: string;
  givenName2: string;
  surname: string;
  surname2: string;
  formerNameStyle: OfacNameStyle | '';
  formerNameStyleLocked?: boolean;
  formerGivenName: string;
  formerGivenName2: string;
  formerSurname: string;
  formerSurname2: string;
  nationalIdType: string;
  nationalId: string;
  locationCountry: string;
  locationOtherCountry: string;
  locationRegion: string;
  locationOtherRegion: string;
  locationCounty: string;
  locationOtherCounty: string;
  locationCity: string;
  locationOtherCity: string;
};

export const EMPTY_RESEARCH_PERSON_FIELDS: ResearchPersonKeyFields = {
  searchCountry: '',
  nationality: '',
  nationalities: '',
  gender: '',
  includeMaidenName: false,
  addressLine: '',
  addressLine2: '',
  postalCode: '',
  nameStyle: '',
  nameStyleLocked: false,
  givenName: '',
  givenName2: '',
  surname: '',
  surname2: '',
  formerNameStyle: '',
  formerNameStyleLocked: false,
  formerGivenName: '',
  formerGivenName2: '',
  formerSurname: '',
  formerSurname2: '',
  nationalIdType: '',
  nationalId: '',
  locationCountry: '',
  locationOtherCountry: '',
  locationRegion: '',
  locationOtherRegion: '',
  locationCounty: '',
  locationOtherCounty: '',
  locationCity: '',
  locationOtherCity: '',
};

export function parseResearchCountryCodes(value: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of String(value || '').split(/[,\s;]+/)) {
    const code = token.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code) || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

export function researchPersonNationalityCodes(fields: Partial<ResearchPersonKeyFields>): string[] {
  const listed = parseResearchCountryCodes(fields.nationalities || '');
  if (listed.length) return listed;
  return parseResearchCountryCodes(fields.nationality || '');
}

export function researchPersonSearchCountry(fields: Partial<ResearchPersonKeyFields>): string {
  const search = String(fields.searchCountry || '').trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(search)) return search;
  const location = String(fields.locationCountry || '').trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(location)) return location;
  return '';
}

export function researchPersonIdCountry(fields: Partial<ResearchPersonKeyFields>): string {
  const nationalities = researchPersonNationalityCodes(fields);
  const origin = nationalities.find((code) => !ofacUsesUsPeopleDirectories(code));
  if (origin) return origin;
  return researchPersonSearchCountry(fields) || nationalities[0] || '';
}

export function researchPersonVisibleFields(searchCountry: string, nationality = ''): {
  showIdentity: boolean;
  showResidence: boolean;
  showNationalId: boolean;
  showUsIdentifiers: boolean;
} {
  const venue = String(searchCountry || '').trim().toUpperCase();
  const origin = String(nationality || '').trim().toUpperCase();
  const us = ofacUsesUsPeopleDirectories(venue);
  return {
    showIdentity: true,
    showResidence: Boolean(venue || origin),
    showNationalId: Boolean(venue || origin),
    showUsIdentifiers: Boolean(venue) && us,
  };
}

export function researchPersonAgeBasis(fields: {
  ageBasis?: string;
  birthdate?: string;
  ageRange?: string;
}): ResearchPersonAgeBasis {
  const stored = String(fields.ageBasis || '').trim();
  if (stored === 'ageRange' || stored === 'birthdate') {
    return stored;
  }
  if (String(fields.ageRange || '').trim() && !String(fields.birthdate || '').trim()) {
    return 'ageRange';
  }
  return 'birthdate';
}

export function applyResearchAgeBasisChange<T extends {
  ageBasis?: ResearchPersonAgeBasis;
  birthdate?: string;
  ageRange?: string;
}>(current: T, basis: ResearchPersonAgeBasis): T {
  if (basis === 'ageRange') {
    return { ...current, ageBasis: 'ageRange', birthdate: '' };
  }
  return { ...current, ageBasis: 'birthdate', ageRange: '' };
}

export function researchPersonShowsMaidenName(fields: {
  gender?: string;
  includeMaidenName?: boolean;
}): boolean {
  return String(fields.gender || '').trim().toLowerCase() === 'female'
    && Boolean(fields.includeMaidenName);
}

export function clearResearchPersonFormerNameFields<T extends Partial<ResearchPersonKeyFields>>(
  fields: T,
): T {
  return {
    ...fields,
    includeMaidenName: false,
    formerNameStyle: '',
    formerNameStyleLocked: false,
    formerGivenName: '',
    formerGivenName2: '',
    formerSurname: '',
    formerSurname2: '',
  };
}

export function applyResearchGenderChange<T extends Partial<ResearchPersonKeyFields>>(
  current: T,
  gender: ResearchPersonGender,
): T {
  const next = { ...current, gender };
  if (gender === 'female') {
    return { ...next, includeMaidenName: false };
  }
  return clearResearchPersonFormerNameFields(next);
}

export function researchPersonAgePayload(fields: {
  ageBasis?: string;
  birthdate?: string;
  ageRange?: string;
}): { birthdate: string; ageRange: string } {
  if (researchPersonAgeBasis(fields) === 'ageRange') {
    return { birthdate: '', ageRange: normalizeAgeRangeForSubmit(fields.ageRange) };
  }
  return { birthdate: String(fields.birthdate || '').trim(), ageRange: '' };
}

function writtenNameFromParts(fields: {
  givenName?: string;
  givenName2?: string;
  surname?: string;
  surname2?: string;
  names?: string;
}): string {
  const joined = [fields.givenName, fields.givenName2, fields.surname, fields.surname2]
    .map((row) => String(row || '').trim())
    .filter(Boolean)
    .join(' ');
  return joined || String(fields.names || '').trim();
}

function nameDetectHints(fields: Partial<ResearchPersonKeyFields>) {
  return {
    nationalities: researchPersonNationalityCodes(fields),
    searchCountry: researchPersonSearchCountry(fields),
  };
}

export function researchPersonNameStyle(fields: Partial<ResearchPersonKeyFields>): OfacNameStyle {
  const stored = String(fields.nameStyle || '').trim();
  if (stored === 'hispanic' || stored === 'portuguese' || stored === 'western') {
    return stored;
  }
  return ofacDetectNameStyle(writtenNameFromParts(fields), {
    ...nameDetectHints(fields),
    surname2: fields.surname2,
  });
}

export function composeResearchPersonLegalName(fields: Partial<ResearchPersonKeyFields>): string {
  return composeOfacLegalName({
    style: researchPersonNameStyle(fields),
    givenName: fields.givenName,
    givenName2: fields.givenName2,
    surname: fields.surname,
    surname2: fields.surname2,
  });
}

/** Former/maiden names detect independently of the current married name. */
export function researchPersonFormerNameStyle(fields: Partial<ResearchPersonKeyFields>): OfacNameStyle {
  const stored = String(fields.formerNameStyle || '').trim();
  if (stored === 'hispanic' || stored === 'portuguese' || stored === 'western') {
    return stored;
  }
  const origin = researchPersonNationalityCodes(fields).find((code) => !ofacUsesUsPeopleDirectories(code)) || '';
  return ofacDetectNameStyle(writtenNameFromParts({
    givenName: fields.formerGivenName,
    givenName2: fields.formerGivenName2,
    surname: fields.formerSurname,
    surname2: fields.formerSurname2,
  }), {
    nationalities: origin ? [origin] : researchPersonNationalityCodes(fields),
    searchCountry: origin,
    surname2: fields.formerSurname2,
  });
}

export function composeResearchPersonFormerName(fields: Partial<ResearchPersonKeyFields>): string {
  return composeOfacLegalName({
    style: researchPersonFormerNameStyle(fields),
    givenName: fields.formerGivenName,
    givenName2: fields.formerGivenName2,
    surname: fields.formerSurname,
    surname2: fields.formerSurname2,
  });
}

export function syncResearchPersonFormerNameFields<T extends Partial<ResearchPersonKeyFields>>(
  fields: T,
): T {
  return {
    ...fields,
    formerNameStyle: fields.formerNameStyleLocked ? researchPersonFormerNameStyle(fields) : '',
  };
}

/** True when the composed place is only a country label (not a street/city residence). */
export function isCountryOnlyResearchPlace(place: string): boolean {
  const text = String(place || '').trim();
  if (!text || text.includes(',')) return false;
  // Single token country names / demonyms used as origin, not an address.
  return /^(united states|usa|u\.s\.a\.|venezuela|venezuelan|mexico|mexican|colombia|colombian|spain|spanish|canada|canadian|brazil|brazilian)$/i.test(text);
}

export function researchPersonOriginCountryCodes(fields: Partial<ResearchPersonKeyFields>): string[] {
  const codes = [...researchPersonNationalityCodes(fields)];
  const seen = new Set(codes);
  for (const candidate of [
    fields.locationCountry,
    fields.searchCountry,
  ]) {
    const code = String(candidate || '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code) || seen.has(code)) continue;
    // Non-US search/residence country is an origin/jurisdiction signal even when
    // citizenship chips only list US (common when operators pick "search in VE").
    if (code !== 'US') {
      seen.add(code);
      codes.push(code);
    }
  }
  return codes;
}

export function composeResearchPersonPlace(
  fields: Partial<ResearchPersonKeyFields>,
  language: OfacLanguage = 'en',
): string {
  const countryCode = String(fields.locationCountry || fields.searchCountry || fields.nationality || '').trim();
  const layout = ofacAddressLayout(countryCode);
  const line = String(fields.addressLine || '').trim();
  const line2 = String(fields.addressLine2 || '').trim();
  const postal = String(fields.postalCode || '').trim();
  const hasLocality = Boolean(
    line
    || line2
    || postal
    || String(fields.locationCity || fields.locationOtherCity || '').trim()
    || String(fields.locationRegion || fields.locationOtherRegion || '').trim()
    || String(fields.locationCounty || fields.locationOtherCounty || '').trim(),
  );
  // Country-only residence (e.g. "Venezuela") is origin, not a street address for
  // people-finder / courts. Keep locationCountry on key_data; omit from addresses.
  if (!hasLocality) return '';
  const geo = composeOfacPlace({
    country: fields.locationCountry || fields.searchCountry || fields.nationality,
    otherCountry: fields.locationOtherCountry,
    region: fields.locationRegion,
    otherRegion: fields.locationOtherRegion,
    county: fields.locationCounty,
    otherCounty: fields.locationOtherCounty,
    city: fields.locationCity,
    otherCity: fields.locationOtherCity,
    language,
  });
  const head: string[] = [];
  if (layout.includes('line') && line) head.push(line);
  if (layout.includes('line2') && line2) head.push(line2);
  const tail: string[] = [];
  if (layout.includes('postal') && postal) tail.push(postal);
  const place = [...head, ...(geo ? [geo] : []), ...tail].filter(Boolean).join(', ');
  return isCountryOnlyResearchPlace(place) ? '' : place;
}

function aliasSearchVariants(fullName: string, style: OfacNameStyle | string): string[] {
  const composed = String(fullName || '').trim();
  if (!composed) return [];
  const tokenCount = composed.split(/\s+/).filter(Boolean).length;
  const resolved = style === 'portuguese' || style === 'western' || style === 'hispanic'
    ? style
    : (tokenCount >= 4 ? 'hispanic' : 'western');
  return [composed, ...ofacLegalNameSearchVariants(composed, resolved)];
}

export function researchFamilyMemberAliasSeeds(
  members: { name?: string; relation?: string }[] | undefined,
): string[] {
  return (members || [])
    .map((row) => {
      const name = String(row.name || '').trim();
      if (!name) return '';
      const relation = String(row.relation || 'other').trim() || 'other';
      return `${name} (${relation})`;
    })
    .filter(Boolean);
}

export function researchPersonSearchAliases(
  fields: Partial<ResearchPersonKeyFields>,
  extraAliases = '',
): string[] {
  const composed = composeResearchPersonLegalName(fields);
  const style = researchPersonNameStyle(fields);
  const former = researchPersonShowsMaidenName(fields)
    ? composeResearchPersonFormerName(fields)
    : '';
  const extras = String(extraAliases || '')
    .split(/[,;]/)
    .map((item) => item.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  const skipCurrent = composed.trim().toLowerCase();
  for (const row of [
    ...ofacLegalNameSearchVariants(composed, style),
    ...aliasSearchVariants(former, researchPersonFormerNameStyle(fields)),
    ...extras.flatMap((item) => aliasSearchVariants(item, item.split(/\s+/).filter(Boolean).length >= 4 ? 'hispanic' : 'western')),
  ]) {
    const key = row.toLowerCase();
    if (!row || key === skipCurrent || seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

export function applyResearchSearchCountryChange<T extends Partial<ResearchPersonKeyFields>>(
  current: T,
  searchCountry: string,
): T {
  const code = String(searchCountry || '').trim().toUpperCase();
  const us = ofacUsesUsPeopleDirectories(code);
  // Do not copy search country into residence — that conflated Venezuela origin
  // with "search venue" and poisoned US people-finder / courts with location=VE.
  return {
    ...current,
    searchCountry: code,
    ...(us ? {} : { ssn: '', dlNumber: '' }),
  };
}

export function applyResearchNationalitiesChange<T extends Partial<ResearchPersonKeyFields>>(
  current: T,
  nationalities: string[],
): T {
  const codes = parseResearchCountryCodes(nationalities.join(','));
  const origin = codes.find((row) => !ofacUsesUsPeopleDirectories(row)) || codes[0] || '';
  const idTypes = ofacNationalIdTypes(origin);
  const searchCountry = current.searchCountry || (codes.includes('US') ? 'US' : '');
  return {
    ...current,
    nationalities: codes.join(','),
    nationality: codes[0] || '',
    searchCountry: searchCountry || current.searchCountry || '',
    nationalIdType: current.nationalIdType || idTypes[0]?.value || '',
  };
}

export function applyResearchNationalityChange<T extends Partial<ResearchPersonKeyFields>>(
  current: T,
  nationality: string,
): T & ResearchPersonKeyFields {
  const code = String(nationality || '').trim().toUpperCase();
  const nextCodes = code ? [code] : [];
  return {
    ...EMPTY_RESEARCH_PERSON_FIELDS,
    ...current,
    ...applyResearchNationalitiesChange(current, nextCodes),
  };
}

export function syncResearchPersonNameFields<T extends Partial<ResearchPersonKeyFields> & { names?: string }>(
  fields: T,
): T {
  const style = researchPersonNameStyle(fields);
  const names = composeOfacLegalName({
    style,
    givenName: fields.givenName,
    givenName2: fields.givenName2,
    surname: fields.surname,
    surname2: fields.surname2,
  }) || String(fields.names || '').trim();
  return {
    ...fields,
    nameStyle: style,
    names,
  };
}
